import { API_ENDPOINT_CHAT, BASE } from '@/api/chatbot';
import { useAppStore } from '@/store/appStore';
import { useLanguageStore } from '@/store/languageStore';
import { useNavigationStore } from '@/store/navigationStore';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface ChatEntry {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
}

const ChatBotPage: React.FC = () => {
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const traceId = useNavigationStore((state) => state.traceId);
  const setTraceId = useNavigationStore((state) => state.setTraceId);
  const userId = useAppStore((state) => state.userId);
  const setUserId = useAppStore((state) => state.setUserId);
  const appLanguage = useLanguageStore((state) => state.appLanguage);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Cleanup on unmount
  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const sendMessage = async () => {
    const userQuery = inputValue.trim();
    if (!userQuery || isStreaming) return;

    // Resolve IDs — store setters are async so use local vars
    const trace = traceId || (() => { const id = uuidv4(); setTraceId(id); return id; })();
    const user = userId || (() => { const id = uuidv4(); setUserId(id); return id; })();

    // Add user bubble only — assistant bubbles are created per update chunk
    setChatHistory((prev) => [
      ...prev,
      { role: 'user', content: userQuery },
    ]);
    setInputValue('');
    setIsStreaming(true);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const url = new URL(API_ENDPOINT_CHAT, BASE).toString();

    await fetchEventSource(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: userQuery,
        trace_id: trace,
        user_id: user,
        lang: appLanguage === 'en' ? 'en' : 'zh_HK',
      }),
      signal: abortControllerRef.current.signal,

      onopen: async (response) => {
        if (!response.ok || !response.headers.get('content-type')?.includes('text/event-stream')) {
          throw new Error(`Unexpected response: ${response.status}`);
        }
      },

      onmessage: (event) => {
        if (!event.data) return;

        try {
          const parsed = JSON.parse(event.data);
          console.log("parsed", parsed)
          if (parsed.event === 'update') {
            // Each update chunk gets its own bubble
            setChatHistory((prev) => [
              ...prev,
              { role: 'assistant', content: parsed.data.message, streaming: false },
            ]);
          } else if (parsed.event === 'end') {
            // Stream is done — just stop the streaming indicator, keep all bubbles
            setIsStreaming(false);
            setChatHistory((prev) => [
              ...prev,
              { role: 'assistant', content: parsed.data.message, streaming: false },
            ]);
            abortControllerRef.current?.abort();
          }
        } catch {
          console.warn('Failed to parse SSE data:', event.data);
        }
      },

      onclose: () => {
        // Stream ended cleanly — ensure streaming state is cleared
        setChatHistory((prev) =>
          prev.map((entry, i) =>
            i === prev.length - 1 ? { ...entry, streaming: false } : entry
          )
        );
        setIsStreaming(false);
      },

      onerror: (err) => {
        console.error('SSE error:', err);
        setChatHistory((prev) =>
          prev.map((entry, i) =>
            i === prev.length - 1
              ? { ...entry, content: 'Something went wrong. Please try again.', streaming: false }
              : entry
          )
        );
        setIsStreaming(false);
        throw err; // Prevent fetchEventSource from auto-retrying
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.headerTitle}>Assistant</span>
        <span style={{ ...styles.statusBadge, ...(isStreaming ? styles.statusActive : styles.statusIdle) }}>
          {isStreaming ? 'Responding…' : 'Ready'}
        </span>
      </div>

      {/* Messages */}
      <div style={styles.messageArea}>
        {chatHistory.length === 0 && (
          <div style={styles.emptyState}>Ask me anything to get started.</div>
        )}
        {chatHistory.map((entry, index) => (
          <div
            key={index}
            style={{ ...styles.messageRow, justifyContent: entry.role === 'user' ? 'flex-end' : 'flex-start' }}
          >
            <div style={{ ...styles.bubble, ...(entry.role === 'user' ? styles.userBubble : styles.assistantBubble) }}>
              {entry.content}
              {entry.streaming && <span style={styles.cursor}>▍</span>}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={styles.inputArea}>
        <textarea
          style={styles.input}
          placeholder="Type your message… (Enter to send, Shift+Enter for new line)"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={isStreaming}
        />
        <button
          style={{ ...styles.sendButton, ...(isStreaming || !inputValue.trim() ? styles.sendButtonDisabled : {}) }}
          onClick={sendMessage}
          disabled={isStreaming || !inputValue.trim()}
        >
          {isStreaming ? '…' : '↑'}
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    maxWidth: '720px',
    margin: '0 auto',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    backgroundColor: '#f9f9f9',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid #e5e5e5',
    backgroundColor: '#fff',
  },
  headerTitle: { fontWeight: 600, fontSize: '16px', color: '#111' },
  statusBadge: { fontSize: '12px', padding: '3px 10px', borderRadius: '999px', fontWeight: 500 },
  statusIdle: { backgroundColor: '#f0f0f0', color: '#666' },
  statusActive: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  messageArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  emptyState: { textAlign: 'center', color: '#aaa', fontSize: '14px', marginTop: '40px' },
  messageRow: { display: 'flex', width: '100%' },
  bubble: {
    maxWidth: '75%',
    padding: '10px 14px',
    borderRadius: '16px',
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  userBubble: { backgroundColor: '#111', color: '#fff', borderBottomRightRadius: '4px' },
  assistantBubble: { backgroundColor: '#fff', color: '#111', border: '1px solid #e5e5e5', borderBottomLeftRadius: '4px' },
  cursor: { display: 'inline-block', marginLeft: '2px' },
  inputArea: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
    padding: '12px 16px',
    borderTop: '1px solid #e5e5e5',
    backgroundColor: '#fff',
  },
  input: {
    flex: 1,
    resize: 'none',
    border: '1px solid #ddd',
    borderRadius: '12px',
    padding: '10px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    lineHeight: '1.5',
    backgroundColor: '#f9f9f9',
  },
  sendButton: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#111',
    color: '#fff',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sendButtonDisabled: { backgroundColor: '#ccc', cursor: 'not-allowed' },
};

export default ChatBotPage;